import React from 'react';
import { Tag } from 'antd';

/**
 * Commandment IV — renders the platform's AI "confidence" for an approver.
 *
 * The backend value is a self-consistency score: agreement across repeated model runs. It
 * measures STABILITY, not correctness (a consistently wrong model scores 100%). So this is
 * never labelled accuracy/probability/confidence-in-the-answer, is never colour-coded by
 * threshold (a green verdict would read as "probably right"), and always carries the
 * disclaimer. Absence is stated plainly — no fabricated figure, no empty tag.
 *
 * `scopeLabel` (optional) states what the score covers, e.g. a batch of rows rather than one row.
 * `runs` / `validSamples` / `unavailableReason` are read when the backend supplies them.
 */
export const AGREEMENT_DISCLAIMER = 'does not indicate the answer is correct';

export function toPercent(confidence) {
    if (typeof confidence !== 'number' || Number.isNaN(confidence)) return null;
    return Math.round(confidence <= 1 ? confidence * 100 : confidence);
}

const noteStyle = { fontSize: 12, color: 'var(--color-text-muted)', margin: 0 };

const AiAgreementIndicator = ({ confidence, runs, validSamples, reasoningRefs, unavailableReason, scopeLabel, approved = false, testId = 'ai-confidence-tag' }) => {
    const pct = toPercent(confidence);
    const notes = Array.isArray(reasoningRefs) ? reasoningRefs.filter((n) => typeof n === 'string' && n.trim()) : [];
    const hasRuns = Number.isFinite(runs) && runs > 0;

    if (pct === null) {
        const text = unavailableReason
            ? `Agreement score could not be computed (${unavailableReason}).${approved ? '' : ' Review manually before approving.'}`
            : approved ? 'Agreement score was not computed for this output.' : 'Agreement score was not computed for this output. Review manually before approving.';
        return (
            <div data-testid="ai-agreement-block">
                <Tag data-testid={testId}>{text}</Tag>
                {scopeLabel && <p style={noteStyle} data-testid="ai-agreement-scope">{scopeLabel}</p>}
            </div>
        );
    }

    return (
        <div data-testid="ai-agreement-block" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Tag data-testid={testId} style={{ alignSelf: 'flex-start' }}>
                {hasRuns ? `Agreement across ${runs} runs: ${pct}%` : `Agreement across runs: ${pct}%`}
            </Tag>
            {scopeLabel && <p style={noteStyle} data-testid="ai-agreement-scope">{scopeLabel}</p>}
            <p style={noteStyle}>
                Agreement across runs {AGREEMENT_DISCLAIMER}.{approved ? '' : ' A human must review before approving.'}
            </p>
            {Number.isFinite(validSamples) && (
                <p style={noteStyle} data-testid="ai-valid-samples">
                    Valid samples compared: {validSamples}{hasRuns ? ` of ${runs}` : ''}
                </p>
            )}
            {notes.length > 0 && (
                <div data-testid="ai-reasoning-notes" style={noteStyle}>
                    What varied across runs:
                    <ul style={{ margin: '2px 0 0', paddingLeft: 18 }}>
                        {notes.map((n, i) => (
                            <li key={i}>{n}</li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default AiAgreementIndicator;
